import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { open_api_with_cookies } from "../../../Utils/network";
import LoadingOverlay from "../../../components/Loading/LoadingOverlay";
import "./AISessionDetail.css";

const AISessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      setIsLoading(true);
      try {
        const res = await open_api_with_cookies.get(
          `/myburgoai/admin/sessions/${sessionId}/`
        );
        setData(res.data);
      } catch (error) {
        console.error("Error fetching session detail:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetail();
  }, [sessionId]);

  if (isLoading) return <LoadingOverlay />;
  if (!data) return <div className="ai-session-detail">Session not found.</div>;

  const { session, user, messages, user_memory, session_memory } = data;

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="ai-session-detail">
      <div className="ai-session-back" onClick={() => navigate("/ai/sessions")}>
        &larr; Back to Sessions
      </div>

      <div className="ai-session-detail-header">
        <div>
          <h2 style={{ margin: 0 }}>{session.title || "Untitled Session"}</h2>
          <span style={{ color: "#9197b3", fontSize: 14 }}>
            {session.user_name}
            {user?.email ? ` · ${user.email}` : ""} &middot; Started {formatDate(session.created)}
          </span>
        </div>
      </div>

      {/* Info cards */}
      <div className="ai-session-info-grid">
        <div className="ai-session-info-card">
          <div className="info-label">Messages</div>
          <div className="info-value">{session.total_messages}</div>
        </div>
        <div className="ai-session-info-card">
          <div className="info-label">Tokens Used</div>
          <div className="info-value">
            {(session.total_tokens_used || 0).toLocaleString()}
          </div>
        </div>
        <div className="ai-session-info-card">
          <div className="info-label">Model</div>
          <div className="info-value">{session.ai_model || "—"}</div>
        </div>
        <div className="ai-session-info-card">
          <div className="info-label">Phase</div>
          <div className="info-value">
            {session.conversation_phase
              ? session.conversation_phase.charAt(0).toUpperCase() +
                session.conversation_phase.slice(1)
              : "—"}
          </div>
        </div>
      </div>

      <div className="ai-session-content">
        {/* Conversation */}
        <div className="ai-conversation">
          <div className="ai-conversation-header">
            Conversation ({messages.length} messages)
          </div>
          <div className="ai-conversation-messages">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`ai-msg ${msg.sender_type}`}
              >
                <div className="ai-msg-bubble">{msg.content}</div>
                <div className="ai-msg-meta">
                  <span>
                    {new Date(msg.created).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  {msg.sender_type === "assistant" && (
                    <>
                      {msg.ai_model && (
                        <span className="ai-msg-badge badge-model">
                          {msg.ai_model.replace("claude-", "").replace("-4-6", "")}
                        </span>
                      )}
                      {msg.safety_level && msg.safety_level !== "safe" && (
                        <span
                          className={`ai-msg-badge badge-safety-${msg.safety_level}`}
                        >
                          {msg.safety_level}
                        </span>
                      )}
                      {msg.thumbs_feedback && (
                        <span
                          className={`ai-msg-badge badge-feedback-${
                            msg.thumbs_feedback === "thumbs_up" ? "up" : "down"
                          }`}
                        >
                          {msg.thumbs_feedback === "thumbs_up" ? "👍" : "👎"}
                        </span>
                      )}
                      {msg.search_results_used && (
                        <span className="ai-msg-badge badge-search">
                          Web search
                        </span>
                      )}
                      {msg.expert_persona_used && (
                        <span className="ai-msg-badge badge-persona">
                          {msg.expert_persona_used}
                        </span>
                      )}
                      {msg.tokens_used > 0 && (
                        <span>{msg.tokens_used.toLocaleString()} tokens</span>
                      )}
                      {msg.response_time_ms && (
                        <span>
                          {msg.response_time_ms > 1000
                            ? `${(msg.response_time_ms / 1000).toFixed(1)}s`
                            : `${msg.response_time_ms}ms`}
                        </span>
                      )}
                    </>
                  )}
                </div>
                {/* Images */}
                {msg.images && msg.images.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {msg.images.map((img) => (
                      <a
                        key={img.id}
                        href={img.image_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={img.image_url}
                          alt={img.file_name || "Image"}
                          style={{
                            maxWidth: 200,
                            maxHeight: 150,
                            borderRadius: 8,
                            objectFit: "cover",
                          }}
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="ai-session-sidebar">
          {/* User profile */}
          {user && (
            <div className="ai-sidebar-card ai-user-profile-card">
              <div className="ai-user-profile-heading">
                <div>
                  <h4>User Profile</h4>
                  <span>#{user.id}</span>
                </div>
                <span
                  className={`ai-profile-app-status ${
                    user.app_installed ? "installed" : "not-observed"
                  }`}
                >
                  {user.app_installed ? "App installed" : "Not observed"}
                </span>
              </div>
              <div className="memory-field">
                <div className="memory-field-label">Name</div>
                <div className="memory-field-value">{user.name || "Unknown"}</div>
              </div>
              <div className="memory-field">
                <div className="memory-field-label">Email</div>
                <div className="memory-field-value">{user.email || "Not provided"}</div>
              </div>
              <div className="memory-field">
                <div className="memory-field-label">Phone</div>
                <div className="memory-field-value">
                  {user.phone
                    ? [user.isd_code, user.phone].filter(Boolean).join(" ")
                    : "Not provided"}
                </div>
              </div>
              <div className="memory-field">
                <div className="memory-field-label">Last app opened</div>
                <div className="memory-field-value">
                  {formatDate(user.last_app_opened_at)}
                </div>
              </div>
              <div className="memory-field">
                <div className="memory-field-label">Last app activity</div>
                <div className="memory-field-value">
                  {formatDate(user.last_app_used_at)}
                </div>
              </div>
              <div className="memory-field">
                <div className="memory-field-label">Last login</div>
                <div className="memory-field-value">{formatDate(user.last_login)}</div>
              </div>
            </div>
          )}

          {/* User Memory */}
          {user_memory && (
            <div className="ai-sidebar-card">
              <h4>User Memory</h4>
              {user_memory.is_onboarded && (
                <div className="memory-field">
                  <div className="memory-field-label">Onboarded</div>
                  <div className="memory-field-value">Yes</div>
                </div>
              )}
              {user_memory.onboarding_data &&
                user_memory.onboarding_data.children && (
                  <div className="memory-field">
                    <div className="memory-field-label">Children</div>
                    <div className="memory-field-value">
                      {user_memory.onboarding_data.children
                        .map((c) => `${c.name} (${c.age})`)
                        .join(", ")}
                    </div>
                  </div>
                )}
              {user_memory.onboarding_data &&
                user_memory.onboarding_data.biggest_concern && (
                  <div className="memory-field">
                    <div className="memory-field-label">Biggest Concern</div>
                    <div className="memory-field-value">
                      {user_memory.onboarding_data.biggest_concern}
                    </div>
                  </div>
                )}
              {user_memory.recurring_themes &&
                user_memory.recurring_themes.length > 0 && (
                  <div className="memory-field">
                    <div className="memory-field-label">Recurring Themes</div>
                    <div className="memory-tags">
                      {user_memory.recurring_themes.map((t, i) => (
                        <span key={i} className="memory-tag">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              {user_memory.cross_session_summary && (
                <div className="memory-field">
                  <div className="memory-field-label">Cross-Session Summary</div>
                  <div className="memory-field-value">
                    {user_memory.cross_session_summary}
                  </div>
                </div>
              )}
              {user_memory.emotional_patterns &&
                user_memory.emotional_patterns.length > 0 && (
                  <div className="memory-field">
                    <div className="memory-field-label">Emotional Patterns</div>
                    <div className="memory-tags">
                      {user_memory.emotional_patterns.slice(0, 5).map((p, i) => (
                        <span key={i} className="memory-tag">
                          {typeof p === "string" ? p : `${p.mood} - ${p.topic}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Session Memory */}
          {session_memory && (
            <div className="ai-sidebar-card">
              <h4>Session Memory</h4>
              {session_memory.summary && (
                <div className="memory-field">
                  <div className="memory-field-label">Summary</div>
                  <div className="memory-field-value">
                    {session_memory.summary}
                  </div>
                </div>
              )}
              {session_memory.key_topics &&
                session_memory.key_topics.length > 0 && (
                  <div className="memory-field">
                    <div className="memory-field-label">Key Topics</div>
                    <div className="memory-tags">
                      {session_memory.key_topics.map((t, i) => (
                        <span key={i} className="memory-tag">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              {session_memory.user_preferences &&
                Object.keys(session_memory.user_preferences).length > 0 && (
                  <div className="memory-field">
                    <div className="memory-field-label">Preferences</div>
                    <div className="memory-field-value">
                      {Object.entries(session_memory.user_preferences)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(", ")}
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Session Info */}
          <div className="ai-sidebar-card">
            <h4>Session Info</h4>
            <div className="memory-field">
              <div className="memory-field-label">Session ID</div>
              <div className="memory-field-value" style={{ fontSize: 11, wordBreak: "break-all" }}>
                {session.session_id}
              </div>
            </div>
            <div className="memory-field">
              <div className="memory-field-label">Status</div>
              <div className="memory-field-value">
                {session.is_active ? "Active" : "Inactive"}
              </div>
            </div>
            <div className="memory-field">
              <div className="memory-field-label">Last Activity</div>
              <div className="memory-field-value">
                {formatDate(session.last_activity)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISessionDetail;
